import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import { KTIcon } from '../../../../../_metronic/helpers'
import { FilterManager } from '../../../../common/filter/FilterManager'
import { CursorPagination } from '../../../../common/pagination/CursorPagination'
import { downloadFile } from '../../../../../utils/downloadFile'
import { getDefaultDateRange } from '../../../../../utils/getDefaultDateRange'
import { getMerchantDetail } from '../MerchantDetail/api'
import {
  exportLedgerOperations,
  getLedgerOperationKinds,
  getLedgerOperations,
  getLedgerWallets,
} from './core/api'
import { buildLedgerHistoryQuery } from './core/buildQuery'
import { DIRECTION_OPTIONS } from './core/consts'
import {
  DEFAULT_LEDGER_HISTORY_FILTERS,
  DEFAULT_LEDGER_HISTORY_PAGE_SIZE,
  LEDGER_HISTORY_PAGE_SIZE_OPTIONS,
} from './core/query.defaults'
import { ILedgerHistoryFilters } from './core/types'
import { ILedgerOperation, ILedgerOperationKind, ILedgerWalletOption } from './core/response.types'
import { ledgerHistoryFiltersError } from './core/validateFilters'
import { operationRowKeys } from './core/operationRowKeys'
import { OperationRow } from './components/OperationRow'
import { LedgerDateRangeFilter } from './components/LedgerDateRangeFilter'

const MerchantLedgerHistoryPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const merchantId = parseInt(id ?? '0', 10)

  // Как на «Завершённые пополнения» (getDefaultDateRange): открыв страницу,
  // видим текущие сутки по МСК (00:00 сегодня — 00:00 завтра), а не пустой
  // фильтр — иначе непонятно, за какой период данные (CPAYMENT-1739 QA).
  const { startDate, endDate } = getDefaultDateRange()
  const initialFilters: ILedgerHistoryFilters = {
    ...DEFAULT_LEDGER_HISTORY_FILTERS,
    dateRange: { from: startDate, to: endDate },
  }

  const [merchantTitle, setMerchantTitle] = useState<string | null>(null)
  const [wallets, setWallets] = useState<ILedgerWalletOption[]>([])
  const [kinds, setKinds] = useState<ILedgerOperationKind[]>([])
  // FilterManager читает defaultValues один раз при монтировании, поэтому он
  // появляется, когда запрос справочника завершился; список при этом грузится
  // сразу и без kinds — это тот же выбор «все типы».
  const [kindsSettled, setKindsSettled] = useState(false)
  const [filters, setFilters] = useState<ILedgerHistoryFilters>(initialFilters)
  const [pageSize, setPageSize] = useState(DEFAULT_LEDGER_HISTORY_PAGE_SIZE)
  const [operations, setOperations] = useState<ILedgerOperation[]>([])
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)

  // Стек курсоров, использованных для загрузки уже показанных страниц:
  // cursors[0] === undefined (первая страница). cursorIndex — текущая
  // позиция. Смена фильтров/размера страницы сбрасывает стек — иначе
  // «Назад» уводил бы на страницу от прошлой выборки.
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined])
  const [cursorIndex, setCursorIndex] = useState(0)
  const [nextCursor, setNextCursor] = useState<string | null>(null)

  const allKindCodes = useMemo(() => kinds.map((k) => k.code), [kinds])
  const kindOptions = useMemo(() => kinds.map((k) => ({ value: k.code, label: k.label })), [kinds])
  const kindLabels = useMemo(
    () => Object.fromEntries(kinds.map((k) => [k.code, k.label])) as Record<string, string>,
    [kinds]
  )
  const filterDefaults: ILedgerHistoryFilters = useMemo(
    () => (allKindCodes.length > 0 ? { ...initialFilters, kinds: allKindCodes } : initialFilters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allKindCodes]
  )

  const rowKeys = useMemo(() => operationRowKeys(operations), [operations])

  const walletOptions = useMemo(
    () => wallets.map((w) => ({ value: w.walletId, label: w.currency ?? String(w.ledger) })),
    [wallets]
  )

  useEffect(() => {
    if (!merchantId) return
    getLedgerWallets(merchantId)
      .then((r) => {
        if (r.data.success) setWallets(r.data.data ?? [])
      })
      .catch(() => {})
  }, [merchantId])

  useEffect(() => {
    getLedgerOperationKinds()
      .then((r) => {
        if (r.data.success) setKinds(r.data.data ?? [])
      })
      .catch(() => {})
      .finally(() => setKindsSettled(true))
  }, [])

  // Только для заголовка «к какому мерчанту относится история» — не зависит
  // от полной карточки, поэтому падение этого запроса не блокирует страницу.
  useEffect(() => {
    if (!merchantId) return
    getMerchantDetail(merchantId)
      .then((r) => {
        if (r.data?.success && r.data.merchant) setMerchantTitle(r.data.merchant.title)
      })
      .catch(() => {})
  }, [merchantId])

  const load = useCallback(
    async (f: ILedgerHistoryFilters, size: number, cursor: string | undefined) => {
      if (!merchantId) return
      setLoading(true)
      try {
        const r = await getLedgerOperations(
          merchantId,
          buildLedgerHistoryQuery(f, size, cursor, allKindCodes)
        )
        if (r.data.success) {
          setOperations(r.data.data.operations)
          setNextCursor(r.data.data.nextCursor)
        } else {
          setOperations([])
          setNextCursor(null)
        }
      } catch {
        setOperations([])
        setNextCursor(null)
      } finally {
        setLoading(false)
      }
    },
    [merchantId, allKindCodes]
  )

  useEffect(() => {
    load(initialFilters, DEFAULT_LEDGER_HISTORY_PAGE_SIZE, undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId])

  const handleApplyFilters = (f: ILedgerHistoryFilters) => {
    const error = ledgerHistoryFiltersError(f)
    if (error) {
      Swal.fire({ icon: 'error', title: 'Ошибка', text: error })
      return
    }
    setFilters(f)
    setCursors([undefined])
    setCursorIndex(0)
    load(f, pageSize, undefined)
  }

  const handlePageSizeChange = (size: number) => {
    setPageSize(size)
    setCursors([undefined])
    setCursorIndex(0)
    load(filters, size, undefined)
  }

  const handleForward = () => {
    if (!nextCursor) return
    const nextIndex = cursorIndex + 1
    setCursors((prev) => {
      const copy = prev.slice(0, nextIndex)
      copy[nextIndex] = nextCursor
      return copy
    })
    setCursorIndex(nextIndex)
    load(filters, pageSize, nextCursor)
  }

  const handleBack = () => {
    if (cursorIndex === 0) return
    const prevIndex = cursorIndex - 1
    setCursorIndex(prevIndex)
    load(filters, pageSize, cursors[prevIndex])
  }

  const handleExport = async () => {
    if (!merchantId) return
    setExporting(true)
    try {
      const { data: blob } = await exportLedgerOperations(
        merchantId,
        buildLedgerHistoryQuery(filters, pageSize, undefined, allKindCodes)
      )
      downloadFile(blob, 'ledger_operations', 'xlsx')
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Ошибка',
        text: 'Не удалось экспортировать историю операций.',
      })
    } finally {
      setExporting(false)
    }
  }

  return (
    <>
      <div className='d-flex align-items-center mb-7'>
        <button
          className='btn btn-sm btn-light me-3'
          onClick={() => navigate(`/settings/merchants/${merchantId}`)}
        >
          <KTIcon iconName='arrow-left' className='fs-4 me-1' />
          К мерчанту
        </button>
        <div>
          <h1 className='page-heading fw-bold fs-2 text-dark mb-0'>
            {merchantTitle ?? `Мерчант #${merchantId}`}
          </h1>
          <div className='text-muted fs-7'>История операций Ledger</div>
        </div>
      </div>
      {kindsSettled && (
        <FilterManager<ILedgerHistoryFilters>
          activeFilters={[
            'dateRange',
            { filter: 'select', fieldName: 'wallet' },
            { filter: 'select', fieldName: 'direction' },
            // Справочник не загрузился — фильтра типа нет, история без него как раньше.
            ...(kinds.length > 0
              ? [{ filter: 'selectMulti' as const, fieldName: 'kinds' as const }]
              : []),
          ]}
          defaultValues={filterDefaults}
          customFilters={{
            dateRange: { component: LedgerDateRangeFilter as any, defaultValue: {} as any },
          }}
          filterProps={{
            dateRange: { className: 'w-100' },
            wallet: { label: 'Кошелёк', options: walletOptions, placeholder: 'Все кошельки' },
            direction: { label: 'Направление', options: DIRECTION_OPTIONS, isSearchable: false },
            kinds: {
              label: 'Тип операции',
              options: kindOptions,
              isSearchable: false,
              placeholder: 'Не выбрано',
              wrapChips: true,
            },
          }}
          onApplyFilters={handleApplyFilters}
        />
      )}
      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title'>Операции</h3>
          <div className='card-toolbar'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              disabled={exporting}
              onClick={handleExport}
            >
              Экспорт в Excel
            </button>
          </div>
        </div>
        <div className='card-body pt-0'>
          <div>
            {operations.map((op, i) => (
              <OperationRow
                key={rowKeys[i]}
                op={op}
                kindLabel={kindLabels[op.kind]}
              />
            ))}
            {!loading && operations.length === 0 && (
              <div className='text-center text-muted py-10'>Операций не найдено</div>
            )}
          </div>
          <CursorPagination
            pageSize={pageSize}
            pageSizeOptions={LEDGER_HISTORY_PAGE_SIZE_OPTIONS}
            canGoBack={cursorIndex > 0}
            canGoForward={nextCursor !== null}
            onBack={handleBack}
            onForward={handleForward}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      </div>
    </>
  )
}

export default MerchantLedgerHistoryPage
